<template>
  <main class="employee-leaves-page">
    <div v-if="isNonResident" class="employee-leaves-page__state" role="status">
      <p>{{ nonResidentMessage }}</p>
    </div>

    <div
      v-else-if="loading"
      class="employee-leaves-page__loading"
      role="status"
      aria-label="Loading leave information"
    >
      <div class="employee-leaves-page__skeleton employee-leaves-page__skeleton--header" />
      <div class="employee-leaves-page__skeleton employee-leaves-page__skeleton--summary" />
      <div class="employee-leaves-page__loading-grid">
        <div class="employee-leaves-page__skeleton employee-leaves-page__skeleton--records" />
        <div class="employee-leaves-page__skeleton employee-leaves-page__skeleton--periods" />
      </div>
    </div>

    <div v-else-if="error" class="employee-leaves-page__state" role="alert">
      <div>
        <strong>{{ permissionDenied ? 'Leave information is unavailable' : 'Could not load leave information' }}</strong>
        <p>{{ error }}</p>
      </div>
      <AppButton
        v-if="!permissionDenied"
        design="new"
        theme="dark"
        size="small"
        @click.native="fetchData()"
      >
        Retry
      </AppButton>
    </div>

    <template v-else>
      <AppPageHeader
        design="new"
        title="Leave overview"
        title-tag="h2"
        subtitle="Paid leave balance, deadlines and history."
      >
        <template #eyebrow>
          <AppStatusBadge
            v-for="(alert, index) in alertBadges"
            :key="`${alert.code}-${index}`"
            design="new"
            compact
            :label="alert.label"
            :tone="alert.tone"
          />
        </template>
        <template #actions>
          <span v-if="refreshing" class="employee-leaves-page__refreshing" role="status">
            Updating…
          </span>
          <AppButton v-if="!isSalesView" design="new" @click.native="openRequestModal">
            Create leave request
          </AppButton>
        </template>
      </AppPageHeader>

      <div v-if="refreshError" class="employee-leaves-page__refresh-error" role="alert">
        <span>{{ refreshError }}</span>
        <AppButton design="new" theme="dark" size="small" @click.native="fetchData({ background: true })">
          Retry
        </AppButton>
      </div>

      <AppCard
        design="new"
        padding="none"
        class="employee-leaves-balance"
        aria-label="Paid leave summary"
      >
        <div
          class="employee-leaves-balance__primary"
          :class="`employee-leaves-balance__primary--${availableTone}`"
        >
          <span>Available today</span>
          <strong>{{ daysLabel(summary.available_today) }}</strong>
          <small>{{ currentServicePeriod }}</small>
        </div>

        <dl class="employee-leaves-balance__facts">
          <div>
            <dt>Earned this service year</dt>
            <dd>{{ daysLabel(summary.earned_this_service_year) }}</dd>
          </div>
          <div>
            <dt>Used</dt>
            <dd>{{ daysLabel(summary.used_days) }}</dd>
          </div>
          <div>
            <dt>Scheduled</dt>
            <dd>{{ daysLabel(summary.scheduled_days) }}</dd>
          </div>
          <div>
            <dt>After scheduled</dt>
            <dd>{{ daysLabel(summary.projected_after_scheduled) }}</dd>
          </div>
        </dl>

        <div class="employee-leaves-balance__oldest">
          <span>Oldest unused</span>
          <strong>{{ daysLabel(summary.oldest_unused_entitlement) }}</strong>
          <small v-if="summary.use_before">Use before {{ formatDate(summary.use_before) }}</small>
          <small v-else>No carried entitlement</small>
          <AppStatusBadge
            v-if="hasOldestEntitlement"
            design="new"
            compact
            :label="oldestDeadlineLabel"
            :tone="oldestDeadlineTone"
          />
        </div>
      </AppCard>

      <div class="employee-leaves-page__content">
        <div class="employee-leaves-page__records">
          <EmployeeLeaveRecordsCard
            title="Leave requests"
            :items="leaveRequests"
            empty-title="No pending requests"
            empty-description="New leave requests will appear here until they are reviewed."
          />

          <EmployeeLeaveRecordsCard
            title="Active leave"
            :items="activeLeave"
            :allow-changes="!isSalesView"
            @mutate="openMutation"
            empty-title="No active leave"
            empty-description="Approved current and upcoming leave will appear here."
          />

          <EmployeeLeaveRecordsCard
            title="Leave history"
            :items="displayedHistory"
            :total-count="history.length"
            empty-title="No leave history"
            empty-description="Completed, rejected and cancelled leave will appear here."
            :collapsible="history.length > historyPreviewLimit"
            :expanded="historyExpanded"
            @toggle="historyExpanded = !historyExpanded"
          />
        </div>

        <AppCard
          design="new"
          title="Entitlement by service year"
          subtitle="Oldest paid leave is consumed first."
          title-tag="h3"
          class="employee-leave-periods"
        >
          <template #actions>
            <AppStatusBadge
              design="new"
              compact
              tone="neutral"
              :label="serviceYearsLabel"
            />
          </template>

          <div v-if="periodsNewestFirst.length" class="employee-leave-periods__timeline">
            <article
              v-for="(period, index) in periodsNewestFirst"
              :key="period.from_date || index"
              class="employee-leave-periods__item"
            >
              <div class="employee-leave-periods__rail">
                <span :class="`employee-leave-periods__marker--${periodStatus(period, index).tone}`" />
              </div>
              <div class="employee-leave-periods__content">
                <div class="employee-leave-periods__heading">
                  <strong>{{ formatDate(period.from_date) }} – {{ formatDate(period.to_date) }}</strong>
                  <div class="employee-leave-periods__actions">
                    <AppStatusBadge
                      design="new"
                      compact
                      :label="periodStatus(period, index).label"
                      :tone="periodStatus(period, index).tone"
                    />
                    <EmployeeLeaveInfoButton :content="periodDeadline(period, index)" />
                  </div>
                </div>
                <div class="employee-leave-periods__summary">
                  <strong>{{ wholeDays(period.used_days) }}/{{ wholeDays(period.earned_days) }}</strong>
                  <span aria-hidden="true">·</span>
                  <span><strong>{{ wholeDays(period.remaining_days) }}</strong> remaining</span>
                </div>
              </div>
            </article>
          </div>

          <AppNoResults
            v-else
            design="new"
            small
            title="No service year data"
            text="Employment date is required to calculate leave entitlement."
            :margin-top="0"
            :margin-bottom="0"
          />
        </AppCard>
      </div>

      <EmployeeLeaveRequestModal
        v-if="!isSalesView"
        ref="requestModal"
        :employee="employee"
        @created="handleRequestCreated"
      />
      <LeaveMutationModal ref="mutation" name="employee-leave-mutation" :approve="true" :submit-request="submitMutation" @saved="handleRequestCreated" />
    </template>
  </main>
</template>

<script>
import { format, isValid, parseISO } from 'date-fns'
import { getEmployeeLeaveWorkspace, getSalesEmployeeLeaveWorkspace, postEmployeesLeaveMutation } from '@/http'
import EmployeeLeaveInfoButton from './EmployeeLeaveInfoButton.vue'
import EmployeeLeaveRecordsCard from './EmployeeLeaveRecordsCard.vue'
import EmployeeLeaveRequestModal from './EmployeeLeaveRequestModal.vue'
import LeaveMutationModal from '@/views/employees/leaves/LeaveMutationModal.vue'

const ALERTS = {
  missing_employment_date: {
    label: 'Employment date is missing',
    tone: 'red',
  },
  overdue_entitlement: {
    label: 'Paid leave entitlement is overdue',
    tone: 'red',
  },
  entitlement_due_soon: {
    label: 'Paid leave entitlement is due soon',
    tone: 'amber',
  },
  insufficient_balance: {
    label: 'Paid leave balance is insufficient',
    tone: 'red',
  },
}

const NON_RESIDENT_MESSAGE = 'Leave information is unavailable for non-resident employees.'

export default {
  name: 'EmployeeLeavesDetail',
  components: {
    EmployeeLeaveInfoButton,
    EmployeeLeaveRecordsCard,
    EmployeeLeaveRequestModal,
    LeaveMutationModal,
  },
  props: {
    employee: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      refreshing: false,
      error: '',
      refreshError: '',
      permissionDenied: false,
      unavailable: false,
      summary: {},
      active: [],
      history: [],
      historyExpanded: false,
      historyPreviewLimit: 6,
      requestId: 0,
    }
  },
  computed: {
    nonResidentMessage: () => NON_RESIDENT_MESSAGE,
    isGeneralManager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    // A General Manager reads a non-resident's leave data, read-only.
    isSalesView() {
      return this.employee.resident === false && this.isGeneralManager
    },
    // Residency drives the leave process; HR has no leave data for a non-resident.
    isNonResident() {
      return this.unavailable || (this.employee.resident === false && !this.isGeneralManager)
    },
    alertBadges() {
      return (this.summary.alerts || []).map(code => ({
        code,
        label: ALERTS[code] ? ALERTS[code].label : code,
        tone: ALERTS[code] ? ALERTS[code].tone : 'neutral',
      }))
    },
    availableTone() {
      return Number(this.summary.available_today || 0) < 0 ? 'red' : 'green'
    },
    currentServicePeriod() {
      const period = this.summary.service_period
      if (!period) return 'Employment date is required to calculate entitlement'
      return `Current service year · ${this.formatDate(period.from_date)} – ${this.formatDate(period.to_date)}`
    },
    hasOldestEntitlement() {
      return Number(this.summary.oldest_unused_entitlement || 0) > 0
    },
    oldestDeadlineTone() {
      if ((this.summary.alerts || []).includes('overdue_entitlement')) return 'red'
      if ((this.summary.alerts || []).includes('entitlement_due_soon')) return 'amber'
      return 'blue'
    },
    oldestDeadlineLabel() {
      if (!this.summary.use_before) return 'No deadline'
      if ((this.summary.alerts || []).includes('overdue_entitlement')) {
        return `Overdue · ${this.formatDate(this.summary.use_before)}`
      }
      return `Use by ${this.formatDate(this.summary.use_before)}`
    },
    displayedHistory() {
      return this.historyExpanded ? this.history : this.history.slice(0, this.historyPreviewLimit)
    },
    leaveRequests() {
      return this.active.filter(item => this.statusCode(item.status) === 'requested')
    },
    activeLeave() {
      return this.active.filter(item => this.statusCode(item.status) !== 'requested')
    },
    periodsNewestFirst() {
      const periods = Array.isArray(this.summary.periods) ? this.summary.periods : []
      return [...periods].reverse()
    },
    serviceYearsLabel() {
      const count = this.periodsNewestFirst.length
      return `${count} service ${count === 1 ? 'year' : 'years'}`
    },
  },
  watch: {
    '$route.params.id': 'handleEmployeeChange',
    'employee.resident'() {
      this.handleEmployeeChange()
    },
  },
  created() {
    this.fetchData()
  },
  beforeDestroy() {
    this.requestId += 1
  },
  methods: {
    openMutation(row, kind) { this.$refs.mutation.open({ ...row, id: row.leave_id }, kind) },
    submitMutation(leave, kind, payload) { return postEmployeesLeaveMutation(leave.id, kind, payload) },
    fetchData({ background = false } = {}) {
      if (this.employee.resident === false && !this.isGeneralManager) {
        this.loading = false
        return Promise.resolve()
      }
      const requestId = ++this.requestId
      if (background) {
        this.refreshing = true
        this.refreshError = ''
      } else {
        this.loading = true
        this.error = ''
        this.permissionDenied = false
        this.historyExpanded = false
      }

      const loadWorkspace = this.isSalesView ? getSalesEmployeeLeaveWorkspace : getEmployeeLeaveWorkspace
      return loadWorkspace(this.$route.params.id)
        .then(response => {
          if (requestId !== this.requestId) return
          const data = response.data || {}
          this.unavailable = data.available === false
          if (this.unavailable) return
          this.summary = data.summary || {}
          this.active = Array.isArray(data.active) ? data.active : []
          this.history = Array.isArray(data.history) ? data.history : []
        })
        .catch(error => {
          if (requestId !== this.requestId) return
          const message = this.errorMessage(error)
          if (background) {
            this.refreshError = message
          } else {
            this.permissionDenied = Boolean(error.response && error.response.status === 403)
            this.error = message
          }
        })
        .finally(() => {
          if (requestId !== this.requestId) return
          this.loading = false
          this.refreshing = false
        })
    },
    handleEmployeeChange() {
      this.unavailable = false
      this.summary = {}
      this.active = []
      this.history = []
      this.fetchData()
    },
    openRequestModal() {
      if (this.$refs.requestModal) this.$refs.requestModal.open()
    },
    handleRequestCreated() {
      this.fetchData({ background: true })
    },
    statusCode(status) {
      let value = status
      if (status && typeof status === 'object') value = status.code || status.value || status.name
      return String(value || '').toLowerCase()
    },
    wholeDays(value) {
      const days = Number(value)
      return Number.isFinite(days) ? Math.floor(days) : 0
    },
    daysLabel(value) {
      const days = this.wholeDays(value)
      return `${days} ${days === 1 ? 'day' : 'days'}`
    },
    parseDate(value) {
      if (!value) return null
      const date = parseISO(value)
      return isValid(date) ? date : null
    },
    formatDate(value) {
      const date = this.parseDate(value)
      return date ? format(date, 'dd MMM yyyy') : value || '—'
    },
    periodDeadline(period, index) {
      if (index > 1) return 'Completed service year kept for leave history'
      if (period.use_before) return `Use before ${this.formatDate(period.use_before)}`
      return 'No entitlement deadline'
    },
    periodStatus(period, index) {
      if (this.summary.service_period && period.from_date === this.summary.service_period.from_date) {
        return { label: 'Current service year', tone: 'blue' }
      }
      if (index > 1) return { label: 'Historical record', tone: 'neutral' }
      if (Number(period.remaining_days || 0) <= 0) {
        return { label: 'Completed', tone: 'green' }
      }
      const deadline = this.parseDate(period.use_before)
      const asOf = this.parseDate(this.summary.as_of) || new Date()
      if (deadline && deadline < asOf) return { label: 'Overdue', tone: 'red' }
      return { label: 'On track', tone: 'green' }
    },
    errorMessage(error) {
      const response = error && error.response
      if (response && response.status === 403) {
        return 'You do not have permission to view this employee’s leave information.'
      }
      const data = response && response.data
      return (data && (data.details || data.detail)) || 'Leave information could not be loaded.'
    },
  },
}
</script>

<style lang="scss">
.employee-leaves-page {
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: $ui-space-20 0 $ui-space-32;
  color: $ui-text;
  font-family: $ui-font-body;
}

.employee-leaves-page__loading {
  display: grid;
  gap: $ui-space-16;
}

.employee-leaves-page__loading-grid,
.employee-leaves-page__content {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(430px, .85fr);
  align-items: start;
  gap: $ui-space-16;
}

.employee-leaves-page__skeleton {
  border: 1px solid $ui-line;
  border-radius: $ui-card-radius;
  background: linear-gradient(90deg, $ui-surface-soft 25%, $ui-surface 50%, $ui-surface-soft 75%);
  background-size: 200% 100%;
  animation: employee-leaves-skeleton 1.4s ease-in-out infinite;
}

.employee-leaves-page__skeleton--header { width: 54%; height: 70px; border: 0; }
.employee-leaves-page__skeleton--summary { height: 154px; }
.employee-leaves-page__skeleton--records { height: 420px; }
.employee-leaves-page__skeleton--periods { height: 320px; }

.employee-leaves-page__state,
.employee-leaves-page__refresh-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $ui-space-16;
  padding: $ui-space-16 $ui-space-20;
  border: 1px solid $ui-red;
  border-radius: $ui-panel-radius;
  background-color: $ui-red-soft;
  color: $ui-red;
}

.employee-leaves-page__state strong {
  color: $ui-ink;
  font-family: $ui-font-heading;
  font-size: $ui-font-size-section-title;
}

.employee-leaves-page__state p {
  margin: $ui-space-4 0 0;
  font-size: $ui-font-size-body;
  line-height: 1.45;
}

.employee-leaves-page__refresh-error {
  margin-bottom: $ui-space-16;
  font-size: $ui-font-size-body;
}

.employee-leaves-page__refreshing {
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
  font-weight: 600;
}

.employee-leaves-balance {
  overflow: hidden;
}

.employee-leaves-balance > .app-card__body {
  display: grid;
  grid-template-columns: minmax(230px, 1.1fr) minmax(420px, 2fr) minmax(210px, .9fr);
  align-items: stretch;
}

.employee-leaves-balance__primary,
.employee-leaves-balance__oldest {
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: $ui-space-16 $ui-space-20;
}

.employee-leaves-balance__primary {
  border-left: 3px solid $ui-green;
}

.employee-leaves-balance__primary--red {
  border-left-color: $ui-red;
}

.employee-leaves-balance__primary > span,
.employee-leaves-balance__primary > small,
.employee-leaves-balance__oldest > span,
.employee-leaves-balance__oldest > small,
.employee-leaves-balance__facts dt {
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
  line-height: 18px;
}

.employee-leaves-balance__primary > strong {
  margin: 3px 0;
  color: $ui-green;
  font-family: $ui-font-heading;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.1;
}

.employee-leaves-balance__primary--red > strong {
  color: $ui-red;
}

.employee-leaves-balance__facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 0;
}

.employee-leaves-balance__facts div {
  display: flex;
  min-width: 0;
  justify-content: center;
  flex-direction: column;
  padding: $ui-space-12 $ui-space-20;
  border-bottom: 1px solid $ui-line;
}

.employee-leaves-balance__facts div:nth-child(odd) {
  border-right: 1px solid $ui-line;
}

.employee-leaves-balance__facts div:nth-last-child(-n + 2) {
  border-bottom: 0;
}

.employee-leaves-balance__facts dt,
.employee-leaves-balance__facts dd {
  margin: 0;
}

.employee-leaves-balance__facts dd {
  margin-top: 2px;
  color: $ui-ink;
  font-family: $ui-font-heading;
  font-size: 15px;
  font-weight: 600;
}

.employee-leaves-balance__oldest {
  border-left: 1px solid $ui-line;
  background-color: $ui-surface-soft;
}

.employee-leaves-balance__oldest > strong {
  margin: 3px 0;
  color: $ui-ink;
  font-family: $ui-font-heading;
  font-size: 18px;
  font-weight: 600;
}

.employee-leaves-balance__oldest .app-status-badge {
  align-self: flex-start;
  margin-top: $ui-space-8;
}

.employee-leaves-page__content {
  margin-top: $ui-space-16;
}

.employee-leaves-page__records {
  display: grid;
  min-width: 0;
  gap: $ui-space-16;
}

.employee-leave-periods {
  min-width: 0;
}

.employee-leave-periods__timeline {
  margin-top: 2px;
}

.employee-leave-periods__item {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  min-width: 0;
}

.employee-leave-periods__rail {
  position: relative;
  display: flex;
  justify-content: center;
}

.employee-leave-periods__rail::after {
  position: absolute;
  top: 16px;
  bottom: -2px;
  width: 1px;
  background-color: $ui-line;
  content: '';
}

.employee-leave-periods__item:last-child .employee-leave-periods__rail::after {
  display: none;
}

.employee-leave-periods__rail span {
  z-index: 1;
  width: 10px;
  height: 10px;
  margin-top: 9px;
  border: 2px solid $ui-surface;
  border-radius: 50%;
  box-shadow: 0 0 0 1px $ui-line-strong;
}

.employee-leave-periods__marker--green { background-color: $ui-green; }
.employee-leave-periods__marker--red { background-color: $ui-red; }
.employee-leave-periods__marker--blue { background-color: $ui-blue; }
.employee-leave-periods__marker--neutral { background-color: $ui-muted; }

.employee-leave-periods__content {
  min-width: 0;
  padding: 0 0 $ui-space-20 $ui-space-8;
}

.employee-leave-periods__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $ui-space-12;
}

.employee-leave-periods__heading > strong {
  color: $ui-ink;
  font-size: $ui-font-size-control;
  font-weight: 600;
  line-height: 18px;
}

.employee-leave-periods__actions {
  display: flex;
  align-items: center;
  gap: $ui-space-8;
}

.employee-leave-periods__summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $ui-space-4 $ui-space-12;
  margin-top: $ui-space-4;
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
}

.employee-leave-periods__summary strong {
  color: $ui-ink;
  font-weight: 600;
}

@keyframes employee-leaves-skeleton {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

@media (max-width: 1180px) {
  .employee-leaves-page__loading-grid,
  .employee-leaves-page__content {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1000px) {
  .employee-leaves-balance > .app-card__body {
    grid-template-columns: minmax(220px, .8fr) minmax(0, 2fr);
  }

  .employee-leaves-balance__oldest {
    grid-column: 1 / -1;
    border-top: 1px solid $ui-line;
    border-left: 0;
  }
}

@media (max-width: 680px) {
  .employee-leaves-page__state,
  .employee-leaves-page__refresh-error {
    align-items: flex-start;
    flex-direction: column;
  }

  .employee-leaves-balance > .app-card__body,
  .employee-leaves-balance__facts {
    grid-template-columns: 1fr;
  }

  .employee-leaves-balance__facts div,
  .employee-leaves-balance__facts div:nth-child(odd),
  .employee-leaves-balance__facts div:nth-last-child(-n + 2) {
    border-right: 0;
    border-bottom: 1px solid $ui-line;
  }

  .employee-leaves-balance__facts div:last-child {
    border-bottom: 0;
  }

  .employee-leaves-balance__oldest {
    grid-column: auto;
  }

  .employee-leave-periods__heading {
    align-items: flex-start;
    flex-direction: column;
    gap: $ui-space-8;
  }
}

@media (prefers-reduced-motion: reduce) {
  .employee-leaves-page__skeleton {
    animation: none;
  }
}
</style>
